import { CircularProgress, Dialog, Grid, IconButton, Menu, MenuItem, Popover, TextField, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { Attachment } from "../../utils/types";
import PublicIcon from "@mui/icons-material/Public";
import AddIcon from "../../assets/action-items/add-attachment.svg";
import { ChangeEvent, useEffect, useMemo, useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/actionItems.css";
import ActionItemService from "../../services/actionItems/ActionItemService";
import LinkIcon from "@mui/icons-material/Link";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useQueryClient } from "@tanstack/react-query";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import DownloadIcon from "../../assets/action-items/attachments-downlaod.svg";
import Delete from "../../assets/action-items/slideOver-delete.svg";
import FullScreenIcon from "../../assets/action-items/fullscreenIcon.svg";
import editActionIcon from "../../assets/action-items/edit.svg";
import deleteActionIcon from "../../assets/action-items/delete.svg";
import useGlobalContext from "../../context/useGlobalContext";
import DocumentViewer from "../Common/DocumentViewer";

type AddMode = "URL" | "FILE" | null;

const ATTACHMENT_ACCEPT = ".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf";

const isImageOrPdf = (file: File) => {
  const type = file.type.toLowerCase();
  if (type === "image/png" || type === "image/jpeg" || type === "application/pdf") return true;
  return /\.(png|jpe?g|pdf)$/i.test(file.name);
};

interface AttachmentListProps {
    attachments: { [key: string]: Attachment };
    onUpdate: (uuid: string, field: keyof Attachment, value: string) => void;
    onRemove: (uuid: string) => void;
    onAdd: () => void;
    isPreview: boolean | undefined;
    apiAttachments?: any[];
    meetingId?: string;
    actionItemId?: string;
    onUploadStatusChange?: (isUploading: boolean) => void;
    pendingAttachments?: File[];
    onAddPendingAttachment?: (file: File) => void;
    openDialog?: boolean;
    canEdit: boolean | undefined;
    onSaveAttachments: (updatedAttachments: {
        [key: string]: Attachment;
    }) => Promise<void>;
    onCancelAttachments: () => void
  }
  
  const AttachmentList: React.FC<AttachmentListProps> = ({
    attachments,
    onUpdate,
    onRemove,
    onAdd,
    isPreview,
    apiAttachments = [],
    actionItemId,
    onUploadStatusChange,
    pendingAttachments = [],
    onAddPendingAttachment,
    openDialog,
    canEdit,
    onSaveAttachments,
    onCancelAttachments,
  }) => {
    const attachmentEntries = Object.entries(attachments);
    const [editingAttachmentId, setEditingAttachmentId] = useState<string | null>(null); 
    const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
    const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
    const [errorMap, setErrorMap] = useState<Record<string, boolean>>({});
    const processedAttachmentsRef = useRef<Set<string>>(new Set());
    const queryClient = useQueryClient();
    const [menuState, setMenuState] = useState<{
      anchor: HTMLElement | null;
      uuid: string | null;
    }>({ anchor: null, uuid: null });
    const [previewDialogKey, setPreviewDialogKey] = useState<string | null>(null);
    const [docViewerOpen, setDocViewerOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<{
      url: string;
      name: string;
      type: string;
    } | null>(null);

    const getFileUrl = async (attachment: Attachment) => {
      if (!attachment.path) return null;

      try {
        const s3Url = await downloadPreview({
          attachmentPath: attachment.path,
        });
        return s3Url;
      } catch (err) {
        showSnackbar("error", "Failed to load file preview");
        return null;
      }
    };

    const isImageFile = (fileName?: string) =>
    /\.(png|jpe?g|gif|webp)$/i.test(fileName || "");
    const { showSnackbar } = useDisplaySnackbar();

    const [_addMode, setAddMode] = useState<AddMode>(null);

    const { featureMap } = useGlobalContext();
    const isUploadFile = featureMap?.task_attachments === true;

    const { mutateAsync: downloadPreview } = ActionItemService.useDownloadAttachment({});

    const ensurePreview = async (uuid: string, attachment: Attachment) => {
      if (!attachment.path) return;
      if (previewUrls[uuid]) return; // already fetched
      if (!isImageFile(attachment.fileName)) return;

      try {
        setLoadingMap(p => ({ ...p, [uuid]: true }));
        const s3Url = await downloadPreview({
          attachmentPath: attachment.path,
        });
        setPreviewUrls(p => ({ ...p, [uuid]: s3Url }));
      } catch {
        setErrorMap(p => ({ ...p, [uuid]: true }));
      } finally {
        setLoadingMap(p => ({ ...p, [uuid]: false }));
      }
    };

    const { mutate: download } = ActionItemService.useDownloadAttachment({
      onSuccess: (s3Url: string, { attachmentPath }) => {
        const link = document.createElement("a");

        // Extract file name from attachmentPath or fallback
        const fileName = attachmentPath.split("/").pop() || "download";

        link.href = s3Url; // directly use S3 URL
        link.download = fileName;
        link.click();
      },
    });

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

      const { mutateAsync: addAttachmentMutation } = ActionItemService.useAddAttachment({
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["get_action_item_details", actionItemId],
          });
        },
        onError: (error: any) => {
          const message = error?.response?.data?.message || error?.message || "Failed to add attachment";
          showSnackbar("error", message);
        },
      });

      useEffect(() => {
        onUploadStatusChange?.(isUploadingFiles);
      }, [isUploadingFiles, onUploadStatusChange]);

      const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (!selectedFiles.length) return;

        const allowedFiles = selectedFiles.filter(isImageOrPdf);
        if (allowedFiles.length !== selectedFiles.length) {
          showSnackbar("error", "Only PNG, JPG, and PDF files can be attached.");
        }
        if (!allowedFiles.length) {
          event.target.value = "";
          return;
        }

        if (!actionItemId) {
          allowedFiles.forEach((file) => onAddPendingAttachment?.(file));
          event.target.value = "";
          return;
        }

        setIsUploadingFiles(true);
        try {
          await Promise.all(
            allowedFiles.map((file) =>
              addAttachmentMutation({
                actionItemId,
                file,
              })
            )
          );

        } catch (err) {
          console.error("Attachment upload failed", err);
        } finally {
          setIsUploadingFiles(false);
          event.target.value = "";
        }
      };
    const fileAttachments = attachmentEntries.filter(
      ([_, attachment]) => !!attachment.path
    );
    const urlAttachments = attachmentEntries.filter(
      ([_, attachment]) => !attachment.path
    );

    const normalizeUrlAttachments = (items: Array<{ url: string; description: string }>) =>
      items
        .map((item) => ({
          url: (item.url || "").trim(),
          description: (item.description || "").trim(),
        }))
        .filter((item) => item.url)
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

    const hasUrlAttachmentChanges = useMemo(() => {
      const currentUrlAttachments = normalizeUrlAttachments(
        urlAttachments.map(([_, attachment]) => ({
          url: attachment.url || "",
          description: attachment.description || "",
        }))
      );

      const apiUrlAttachments = normalizeUrlAttachments(
        (apiAttachments || [])
          .filter((attachment: any) => !attachment?.path)
          .map((attachment: any) => ({
            url: attachment?.url || "",
            description: attachment?.description || "",
          }))
      );

      return JSON.stringify(currentUrlAttachments) !== JSON.stringify(apiUrlAttachments);
    }, [urlAttachments, apiAttachments]);

    const { mutate: deleteAttachmentMutation } = ActionItemService.useDeleteAttachment({
      onSuccess: () => {
        // After deletion, remove it from local state
        if (menuState.uuid) {
          onRemove(menuState.uuid);
          queryClient.invalidateQueries({
            queryKey: ["get_action_item_details", actionItemId],
          });
        }
        showSnackbar("success", "Successfully Delete the attachment.")
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.message || "Failed to delete attachment";
        showSnackbar("error", message);
      },
    });

    useEffect(() => {
      // Get current attachment UUIDs
      const currentUuids = new Set(fileAttachments.map(([uuid]) => uuid));
      
      // Clean up processed attachments that no longer exist
      processedAttachmentsRef.current.forEach(uuid => {
        if (!currentUuids.has(uuid)) {
          processedAttachmentsRef.current.delete(uuid);
        }
      });
      
      // Clean up preview URLs and states for deleted attachments
      setPreviewUrls(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        Object.keys(prev).forEach(uuid => {
          if (!currentUuids.has(uuid)) {
            delete updated[uuid];
            hasChanges = true;
          }
        });
        
        return hasChanges ? updated : prev;
      });
      
      setLoadingMap(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        Object.keys(prev).forEach(uuid => {
          if (!currentUuids.has(uuid)) {
            delete updated[uuid];
            hasChanges = true;
          }
        });
        
        return hasChanges ? updated : prev;
      });
      
      setErrorMap(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        Object.keys(prev).forEach(uuid => {
          if (!currentUuids.has(uuid)) {
            delete updated[uuid];
            hasChanges = true;
          }
        });
        
        return hasChanges ? updated : prev;
      });
      
      // Process ONLY new attachments that haven't been processed before
      fileAttachments.forEach(([uuid, attachment]) => {
        if (
          attachment.path &&
          isImageFile(attachment.fileName) &&
          !processedAttachmentsRef.current.has(uuid)
        ) {
          // Mark as processed immediately to prevent duplicate requests
          processedAttachmentsRef.current.add(uuid);
          ensurePreview(uuid, attachment);
        }
      });
    }, [fileAttachments]);

    useEffect(() => {
      if (!openDialog) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }, [openDialog]);

    const isDuplicateUrl = (uuid: string, url: string) => {
      if (!url) return false;

      const normalized = url.trim().toLowerCase();

      return Object.entries(attachments).some(([id, att]) => {
        if (id === uuid) return false;
        return (att.url || "").trim().toLowerCase() === normalized;
      });
    };

    const isValidUrl = (url: string): boolean => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };

    const hasInvalidUrls = Object.values(attachments).some(
      (att) => att.url && !isValidUrl(att.url)
    );

    const hasEmptyUrls = Object.values(attachments).some(
      (att) => !att.path && !att.url?.trim()
    );

    const hasDuplicateUrls = Object.entries(attachments).some(
      ([uuid, att]) => isDuplicateUrl(uuid, att.url)
    );

    const isSaveDisabled = hasInvalidUrls || hasEmptyUrls || hasDuplicateUrls;
    
    return (
      <>
      <Box sx={{ mb: 3 }}>
        <Box className="attachment-container">
          {!isPreview && (
          <>
          <Typography className="field-label">
            Attachments
          </Typography>
            <Tooltip title="Add attachments" placement="top" arrow>
              <IconButton
                onClick={handleOpenPopover}
                size="small"
                disabled={isPreview || isUploadingFiles}
                disableRipple
              >
                <img src={AddIcon} alt="Add" className="add-icon"/>
              </IconButton>
            </Tooltip>
          </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
        </Box>
        {pendingAttachments.map(file => (
          <Typography key={file.name}>{file.name} </Typography>
        ))}
  
        {urlAttachments.map(([uuid, attachment]) => {
          const isDuplicate = isDuplicateUrl(uuid, attachment.url);
          const isFromApi = attachment.isFromApi;
  
          if (isFromApi) {
            const isEditing = editingAttachmentId === uuid;
            return (
              <Grid container spacing={2} key={uuid} sx={{ mb: 2, alignItems: "center" }}>
                {isEditing ? (
                  <>
                    {/* Editable URL */}
                    <Grid item xs={5.5}>
                      <TextField
                        fullWidth
                        label="URL *"
                        variant="outlined"
                        value={attachment.url}
                        onChange={(e) => onUpdate(uuid, "url", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText={" "}
                        FormHelperTextProps={{ style: { minHeight: 18 } }}
                      />
                    </Grid>
          
                    {/* Editable Description */}
                    <Grid item xs={5.5}>
                      <TextField
                        fullWidth
                        label="Description"
                        variant="outlined"
                        value={attachment.description}
                        onChange={(e) => onUpdate(uuid, "description", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText={" "}
                        FormHelperTextProps={{ style: { minHeight: 18 } }}
                      />
                    </Grid>
          
                    {/* Cancel Editing */}
                    <Grid item xs={1} sx={{alignItems: "center", textAlign: "center"}}>
                      <Tooltip title="Cancel" placement="top" arrow>
                        <IconButton size="small" onClick={() => setEditingAttachmentId(null)} sx={{textAlign: "center"}}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* Read Only Card */}
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box
                        onClick={
                          !isPreview
                            ? () => window.open(attachment.url, "_blank")
                            : undefined // disable click in preview mode
                        }
                        className="attachment-card url-attachment-card"
                        style={{
                          cursor: isPreview ? "default" : "pointer",
                          opacity: isPreview ? 0.6 : 1, // optional visual cue
                          pointerEvents: isPreview ? "none" : "auto", // prevent interaction
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                          <PublicIcon color="action" />
                          <Typography className="attachment-description" noWrap>
                            {attachment.description || "Untitled Attachment"}
                          </Typography>
                        </Box>
                        {!isPreview && (
                          <Box
                            className="url-attachment-actions"
                            sx={{ display: "flex", alignItems: "center", gap: "2px" }}
                          >
                            <Tooltip title="Edit attachment" placement="top" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingAttachmentId(uuid);
                                }}
                                className="linked-interaction-edit-btn"
                              >
                                <Box
                                  component="img"
                                  src={editActionIcon}
                                  alt="edit"
                                  className="linked-interaction-action-icon"
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove attachment" placement="top" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemove(uuid);
                                }}
                                className="linked-interaction-delete-btn"
                              >
                                <Box
                                  component="img"
                                  src={deleteActionIcon}
                                  alt="delete"
                                  className="linked-interaction-action-icon"
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Grid>
          
                  </>
                )}
              </Grid>
            );
          }
  
          return (
            <Grid container spacing={2} key={uuid} sx={{ mb: 2 }} alignItems="center">
              <Grid item xs={5.5}>
                <TextField
                  fullWidth
                  label="URL *"
                  placeholder="https://example.com"
                  variant="outlined"
                  value={attachment.url}
                  onChange={(e) => onUpdate(uuid, "url", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={isPreview}
                  error={attachment.urlError || isDuplicate}
                  helperText={
                    attachment.urlError
                      ? "Please enter a valid URL"
                      : isDuplicate
                      ? "URL already exists. Please add a new one"
                      : " "
                  }
                  FormHelperTextProps={{ style: { minHeight: 18 } }} 
                  sx={{ "& .MuiInputBase-root": { height: "42px" } }}
                />
              </Grid>
              <Grid item xs={5.5}>
                <TextField
                  fullWidth
                  label="Description"
                  variant="outlined"
                  placeholder="Add description"
                  value={attachment.description}
                  onChange={(e) => onUpdate(uuid, "description", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={isPreview}
                  helperText={" "}
                  FormHelperTextProps={{ style: { minHeight: 18 } }} 
                  sx={{ "& .MuiInputBase-root": { height: "42px" } }}
                />
              </Grid>
            </Grid>
          );
        })}
        {!isPreview && canEdit && hasUrlAttachmentChanges && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              width: "100%",
              mb: 2,
            }}
          >
            <button
              className={`btn btn-purple-compact ${isSaveDisabled ? "disabled-look" : ""}`}
              onClick={() => {
                const hasDuplicateUrls = Object.entries(attachments).some(
                  ([uuid, att]) => isDuplicateUrl(uuid, att.url)
                );

                if (hasDuplicateUrls) {
                  showSnackbar("error", "Duplicate URLs are not allowed");
                  return;
                }

                onSaveAttachments(attachments);
                setEditingAttachmentId(null);
              }}
            >
              Save
            </button>
            <button
              className="btn btn-outlined-small"
              onClick={() => {
                onCancelAttachments?.();
                setEditingAttachmentId(null);
              }}
            >
              Cancel
            </button>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {fileAttachments.map(([uuid, attachment]) => {
            const preview = previewUrls[uuid];
            const loading = loadingMap[uuid];
            const error = errorMap[uuid];

            const handleMenuOpen = async (
              e: React.MouseEvent<HTMLElement>,
              uuid: string,
              attachment: Attachment
            ) => {
              e.stopPropagation();
              setMenuState({ anchor: e.currentTarget, uuid });
              await ensurePreview(uuid, attachment);
            };

            const handleMenuClose = () => {
              setMenuState({ anchor: null, uuid: null });
            };

            const handleExpand = async (uuid: string, attachment: Attachment) => {
              await ensurePreview(uuid, attachment);
              setMenuState({ anchor: null, uuid: null });
              setPreviewDialogKey(uuid);
            };

            const handleDownloadClick = () => {
              setMenuState({ anchor: null, uuid: null });
              download({ attachmentPath: attachment.path! });
            };

            const handleDeleteClick = (e?: React.MouseEvent) => {
              e?.stopPropagation();
              if (!actionItemId || !menuState.uuid) return;

              const uuid = menuState.uuid;
              const attachment = attachments[uuid];
              if (!attachment?.path) return;

              deleteAttachmentMutation({
                actionItemId,
                attachmentPath: attachment.path,
              });
            };

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={uuid}>
              <Box className="attachments-grid"
              >
                <Box
                  className={`attachment-image-box ${menuState.uuid === uuid ? 'menu-open' : ''}`}
                  sx={{
                    position: 'relative',
                  }}
                >
                  {loading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: "none",
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  {!loading && preview && isImageFile(attachment.fileName) && (
                    <img
                      src={preview}
                      alt={attachment.fileName}
                    />
                  )}
                  {!loading && preview && !isImageFile(attachment.fileName) && (
                    <iframe
                      src={preview}
                      title={attachment.fileName}
                      style={{
                        width: "100%",
                        height: "180px",
                        border: "none",
                        borderRadius: "6px",
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {!loading && error && (
                    <Typography fontSize={12} color="red">
                      Error
                    </Typography>
                  )}
                  <IconButton
                    size="small"
                    className="menu-icon"
                    onClick={(e) => handleMenuOpen(e, uuid, attachment)}
                    disableRipple
                    color="inherit"
                  >
                    ...
                  </IconButton>
                  <IconButton 
                    className="center-icon"
                    size="small"
                    onClick={async () => {
                      if (isImageFile(attachment.fileName)) {
                        await ensurePreview(uuid, attachment);
                        handleExpand(uuid, attachment);
                      } else {
                        const fileUrl = await getFileUrl(attachment);
                        if (!fileUrl) return;
                        setSelectedDoc({
                          url: fileUrl,
                          name: attachment.fileName || "",
                          type: attachment.fileName?.split(".").pop() || "",
                        });

                        setDocViewerOpen(true);
                      }
                    }}
                    disableRipple
                  >
                    <img src={FullScreenIcon} alt="FullView" />
                  </IconButton>
                  <Menu
                    anchorEl={menuState.anchor}
                    open={menuState.uuid === uuid}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={handleDownloadClick}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <img src={DownloadIcon} alt="Download" />
                        <Typography variant="body2" color="#575763">Download</Typography>
                      </Box>
                    </MenuItem>
                    {canEdit && (
                    <MenuItem onClick={(e) => handleDeleteClick(e)}
                      sx={{
                        color: "#E84646",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <img src={Delete} alt="Delete" />
                        <Typography variant="body2" color="#E84646">Delete</Typography>
                      </Box>
                    </MenuItem>
                    )}
                  </Menu>
                </Box>
                <Tooltip title={attachment.fileName} arrow>
                  <Typography
                    className="attachment-filename"
                    noWrap
                  >
                    {attachment.fileName}
                  </Typography>
                </Tooltip>
                <Typography
                  className="attachments-date"
                >
                {new Date(attachment.uploadedAt ?? Date.now()).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
                </Typography>
                <Dialog 
                  open={previewDialogKey === uuid}
                  onClose={() => setPreviewDialogKey(null)} 
                  maxWidth="md"
                >
                  <Box sx={{ position: "relative", width: "900px", height: "700px" }}>
                  <IconButton
                    onClick={() => setPreviewDialogKey(null)}
                    sx={{ position: "absolute", top: 2, right: 2, zIndex: 1, backgroundColor:"#F2F2F4", ":hover": { backgroundColor: "#F2F2F4" } }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <img
                    src={preview}
                    alt={attachment.fileName}
                    decoding="async"
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "contain", }}
                  />
                </Box>
                </Dialog>
              </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "column",  
            alignItems: "flex-start",
            gap: 1,
            minWidth: "120px",
            borderRadius: "8px",    
          }}
        >
          {isUploadFile && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
              onClick={() => {
                setAddMode("FILE");
                handleClosePopover();
                fileInputRef.current?.click();
              }}
            >
              <FileUploadIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "12px", color: "#575763" }}>
                Upload File
              </Typography>
            </Box>
          )}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
            onClick={() => {
              setAddMode("URL");
              handleClosePopover();
              onAdd();
            }}
          >
            <LinkIcon fontSize="small"/>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "12px", color: "#575763" }}>
              Add URL
            </Typography>
          </Box>
        </Box>
      </Popover>
      {selectedDoc && (
        <DocumentViewer
          open={docViewerOpen}
          onClose={() => {
            setDocViewerOpen(false);
            setSelectedDoc(null);
          }}
          fileUrl={selectedDoc.url}
          fileName={selectedDoc.name}
          fileType={selectedDoc.type}
        />
      )}
      </>
    );
  }; 

  export default AttachmentList
